import React, { useState, useEffect } from 'react';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Percent, Gift, Tag, Users, Clock, Calendar, Zap, 
  Plus, Trash2, Edit, ToggleLeft, Copy, Star,
  TrendingUp, ShoppingBag, Crown, Heart, Coffee
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const OffersManager = ({ storeId, isOpen, onClose }) => {
  const [offers, setOffers] = useState([]);
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const [offerForm, setOfferForm] = useState({
    name: '',
    type: 'PERCENT_DISCOUNT',
    value: '',
    minOrderValue: '',
    maxDiscount: '',
    description: '',
    code: '',
    startDate: '',
    endDate: '',
    applicableItems: 'all', // all, category, specific
    selectedItems: [],
    selectedCategory: '',
    usageLimit: '',
    perCustomerLimit: '',
    enabled: true,
    autoApply: false,
    // BOGO specific
    buyQty: 1,
    getQty: 1,
    getItemId: '',
    // Combo specific
    comboItems: [],
    comboPrice: ''
  });

  // Offer types
  const offerTypes = [
    { value: 'PERCENT_DISCOUNT', label: 'Percentage Discount', icon: Percent, color: 'bg-blue-500', description: 'X% off on order' },
    { value: 'FLAT_DISCOUNT', label: 'Flat/Cash Discount', icon: Tag, color: 'bg-green-500', description: '₹X off on order' },
    { value: 'COMPLIMENTARY', label: 'Complimentary Order', icon: Heart, color: 'bg-pink-500', description: 'Mark order as FREE' },
    { value: 'BOGO', label: 'Buy One Get One', icon: Gift, color: 'bg-purple-500', description: 'Buy X Get Y Free' },
    { value: 'COMBO', label: 'Combo Offer', icon: ShoppingBag, color: 'bg-orange-500', description: 'Bundle items at special price' },
    { value: 'HAPPY_HOUR', label: 'Happy Hour', icon: Clock, color: 'bg-yellow-500', description: 'Time-based discount' },
    { value: 'FIRST_ORDER', label: 'First Order Discount', icon: Star, color: 'bg-indigo-500', description: 'Discount for new customers' },
    { value: 'LOYALTY', label: 'Loyalty Reward', icon: Crown, color: 'bg-amber-500', description: 'Reward for repeat customers' },
    { value: 'BULK_DISCOUNT', label: 'Bulk/Quantity Discount', icon: TrendingUp, color: 'bg-teal-500', description: 'Discount on quantity' },
    { value: 'CASHBACK', label: 'Cashback Offer', icon: Zap, color: 'bg-cyan-500', description: 'Cashback on payment' }
  ];

  useEffect(() => {
    if (isOpen && storeId) {
      loadOffers();
      loadMenuItems();
    }
  }, [isOpen, storeId]);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/offers?store_id=${storeId}`);
      setOffers(response.data || []);
    } catch (error) {
      console.error('Error loading offers:', error);
      // Fallback to localStorage for offline support
      const saved = localStorage.getItem(`offers_${storeId}`);
      if (saved) {
        setOffers(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMenuItems = async () => {
    try {
      const response = await apiClient.get(`/menu/${storeId}`);
      setMenuItems(response.data || []);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const handleAddOffer = async () => {
    if (!offerForm.name || !offerForm.type) {
      toast.error('Please fill required fields');
      return;
    }

    const offerData = {
      store_id: storeId,
      ...offerForm,
      value: parseFloat(offerForm.value) || 0,
      minOrderValue: parseFloat(offerForm.minOrderValue) || 0,
      maxDiscount: parseFloat(offerForm.maxDiscount) || 0,
      usageLimit: parseInt(offerForm.usageLimit) || 0,
      perCustomerLimit: parseInt(offerForm.perCustomerLimit) || 0,
      comboPrice: parseFloat(offerForm.comboPrice) || 0,
      buyQty: parseInt(offerForm.buyQty) || 1,
      getQty: parseInt(offerForm.getQty) || 1,
    };

    try {
      if (editingOffer) {
        await apiClient.put(`/offers/${editingOffer.id}`, offerData);
        toast.success('Offer updated!');
      } else {
        await apiClient.post('/offers', offerData);
        toast.success('Offer created!');
      }
      loadOffers();
      resetForm();
    } catch (error) {
      console.error('Error saving offer:', error);
      let errorMessage = 'Failed to save offer';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    try {
      await apiClient.delete(`/offers/${offerId}`);
      toast.success('Offer deleted');
      loadOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };

  const handleToggleOffer = async (offerId) => {
    try {
      await apiClient.post(`/offers/${offerId}/toggle`);
      toast.success('Offer status updated');
      loadOffers();
    } catch (error) {
      console.error('Error toggling offer:', error);
      toast.error('Failed to update offer');
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({
      ...offer,
      value: offer.value?.toString() || '',
      minOrderValue: offer.min_order_value?.toString() || '',
      maxDiscount: offer.max_discount?.toString() || '',
      usageLimit: offer.usage_limit?.toString() || '',
      perCustomerLimit: offer.per_customer_limit?.toString() || ''
    });
    setShowAddOffer(true);
  };

  const handleDuplicateOffer = (offer) => {
    setOfferForm({
      ...offer,
      name: `${offer.name} (Copy)`,
      code: offer.code ? `${offer.code}_COPY` : '',
      value: offer.value?.toString() || '',
      minOrderValue: offer.min_order_value?.toString() || '',
      maxDiscount: offer.max_discount?.toString() || '',
      usageLimit: offer.usage_limit?.toString() || '',
      perCustomerLimit: offer.per_customer_limit?.toString() || ''
    });
    setEditingOffer(null);
    setShowAddOffer(true);
  };

  const resetForm = () => {
    setOfferForm({
      name: '',
      type: 'PERCENT_DISCOUNT',
      value: '',
      minOrderValue: '',
      maxDiscount: '',
      description: '',
      code: '',
      startDate: '',
      endDate: '',
      applicableItems: 'all',
      selectedItems: [],
      selectedCategory: '',
      usageLimit: '',
      perCustomerLimit: '',
      enabled: true,
      autoApply: false,
      buyQty: 1,
      getQty: 1,
      getItemId: '',
      comboItems: [],
      comboPrice: ''
    });
    setEditingOffer(null);
    setShowAddOffer(false);
  };

  const generateCode = () => {
    const code = `SSPICE${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setOfferForm({ ...offerForm, code });
  };

  const getOfferTypeInfo = (type) => {
    return offerTypes.find(t => t.value === type) || offerTypes[0];
  };

  const filteredOffers = offers.filter(offer => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return offer.enabled;
    if (activeTab === 'inactive') return !offer.enabled;
    return offer.type === activeTab;
  });

  const categories = [...new Set(menuItems.map(item => item.category).filter(Boolean))];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-500" /> Offers & Discounts Manager
            </span>
            <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => setShowAddOffer(true)}>
              <Plus className="h-4 w-4 mr-1" /> Create Offer
            </Button>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="all">All ({offers.length})</TabsTrigger>
            <TabsTrigger value="active">Active ({offers.filter(o => o.enabled).length})</TabsTrigger>
            <TabsTrigger value="inactive">Inactive</TabsTrigger>
            <TabsTrigger value="PERCENT_DISCOUNT">% Discount</TabsTrigger>
            <TabsTrigger value="FLAT_DISCOUNT">Cash Discount</TabsTrigger>
            <TabsTrigger value="COMPLIMENTARY">Complementary</TabsTrigger>
            <TabsTrigger value="BOGO">BOGO</TabsTrigger>
            <TabsTrigger value="COMBO">Combo</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[50vh]">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading offers...</div>
            ) : filteredOffers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No offers found</p>
                <p className="text-sm">Create your first offer to attract customers!</p>
                <Button className="mt-4 bg-purple-500" onClick={() => setShowAddOffer(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Create First Offer
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredOffers.map((offer) => {
                  const typeInfo = getOfferTypeInfo(offer.type);
                  return (
                    <Card key={offer.id} className={`relative ${!offer.enabled ? 'opacity-60' : ''}`}>
                      <div className={`absolute top-0 left-0 right-0 h-1 ${typeInfo.color} rounded-t-lg`} />
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`${typeInfo.color} p-2 rounded-lg`}>
                              <typeInfo.icon className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-sm">{offer.name}</h3>
                              <p className="text-xs text-gray-500">{typeInfo.label}</p>
                            </div>
                          </div>
                          <Badge variant={offer.enabled ? 'default' : 'secondary'}>
                            {offer.enabled ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>

                        <div className="space-y-1 text-sm mb-3">
                          {offer.type === 'PERCENT_DISCOUNT' && (
                            <p className="font-semibold text-blue-600">{offer.value}% OFF</p>
                          )}
                          {offer.type === 'FLAT_DISCOUNT' && (
                            <p className="font-semibold text-green-600">₹{offer.value} OFF</p>
                          )}
                          {offer.type === 'BOGO' && (
                            <p className="font-semibold text-purple-600">Buy {offer.buy_qty} Get {offer.get_qty} FREE</p>
                          )}
                          {offer.type === 'COMPLIMENTARY' && (
                            <p className="font-semibold text-pink-600">🎁 COMPLIMENTARY ORDER - No Charge</p>
                          )}
                          {offer.type === 'COMBO' && (
                            <p className="font-semibold text-orange-600">Combo @ ₹{offer.combo_price}</p>
                          )}
                          {offer.type === 'HAPPY_HOUR' && (
                            <p className="font-semibold text-yellow-600">{offer.value}% OFF (Time Limited)</p>
                          )}
                          {offer.type === 'CASHBACK' && (
                            <p className="font-semibold text-cyan-600">₹{offer.value} Cashback</p>
                          )}
                          
                          {offer.min_order_value > 0 && (
                            <p className="text-xs text-gray-500">Min order: {formatCurrency(offer.min_order_value)}</p>
                          )}
                          {offer.max_discount > 0 && (
                            <p className="text-xs text-gray-500">Max discount: {formatCurrency(offer.max_discount)}</p>
                          )}
                          {offer.code && (
                            <p className="text-xs"><span className="bg-gray-100 px-2 py-0.5 rounded font-mono">{offer.code}</span></p>
                          )}
                        </div>

                        {offer.description && (
                          <p className="text-xs text-gray-600 mb-3">{offer.description}</p>
                        )}

                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleToggleOffer(offer.id)}>
                            <ToggleLeft className="h-3 w-3 mr-1" />
                            {offer.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleEditOffer(offer)}>
                            <Edit className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleDuplicateOffer(offer)}>
                            <Copy className="h-3 w-3 mr-1" /> Copy
                          </Button>
                          <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDeleteOffer(offer.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </Tabs>

        {/* Add/Edit Offer Dialog */}
        <Dialog open={showAddOffer} onOpenChange={() => resetForm()}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOffer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Offer Type Selection */}
              <div>
                <Label className="mb-2 block">Offer Type *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {offerTypes.map((type) => (
                    <Card 
                      key={type.value}
                      className={`cursor-pointer transition-all ${offerForm.type === type.value ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:bg-gray-50'}`}
                      onClick={() => setOfferForm({ ...offerForm, type: type.value })}
                    >
                      <CardContent className="p-3 flex items-center gap-2">
                        <div className={`${type.color} p-1.5 rounded`}>
                          <type.icon className="h-3 w-3 text-white" />
                        </div>
                        <div>
                          <p className="text-xs font-medium">{type.label}</p>
                          <p className="text-[10px] text-gray-500">{type.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Offer Name *</Label>
                  <Input 
                    placeholder="e.g., Weekend Special"
                    value={offerForm.name}
                    onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Promo Code</Label>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="SAVE20"
                      value={offerForm.code}
                      onChange={(e) => setOfferForm({ ...offerForm, code: e.target.value.toUpperCase() })}
                    />
                    <Button type="button" variant="outline" onClick={generateCode}>Generate</Button>
                  </div>
                </div>
              </div>

              {/* COMBO Offer Configuration - MOVED UP for visibility */}
              {offerForm.type === 'COMBO' && (
                <div className="space-y-4 p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-800 font-semibold">
                    <ShoppingBag className="h-5 w-5" />
                    <span>Combo Configuration</span>
                  </div>
                  
                  <div>
                    <Label>Combo Price (₹) *</Label>
                    <Input 
                      type="number"
                      placeholder="299"
                      value={offerForm.comboPrice}
                      onChange={(e) => setOfferForm({ ...offerForm, comboPrice: e.target.value })}
                      className="bg-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Special price when all combo items are ordered together</p>
                  </div>
                  
                  <div className="bg-white border border-orange-200 rounded-lg p-3 space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-orange-800 font-medium">Combo Items *</Label>
                      <Badge variant="outline" className="bg-orange-100 text-orange-800">
                        {offerForm.comboItems.length} items added
                      </Badge>
                    </div>
                    
                    {/* Add Item to Combo */}
                    <Select 
                      onValueChange={(itemId) => {
                        const selectedItem = menuItems.find(i => i.id === itemId);
                        if (selectedItem && !offerForm.comboItems.find(c => c.item_id === itemId)) {
                          setOfferForm({
                            ...offerForm,
                            comboItems: [
                              ...offerForm.comboItems,
                              { item_id: itemId, item_name: selectedItem.name, quantity: 1, price: selectedItem.price }
                            ]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white border-orange-300">
                        <SelectValue placeholder="➕ Click to add item to combo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {menuItems
                          .filter(i => i.active && !offerForm.comboItems.find(c => c.item_id === i.id))
                          .map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} - ₹{item.price}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    
                    {/* Combo Items List */}
                    {offerForm.comboItems.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {offerForm.comboItems.map((comboItem, idx) => (
                          <div key={comboItem.item_id} className="flex items-center justify-between bg-orange-50 p-2 rounded border border-orange-200">
                            <span className="text-sm font-medium truncate flex-1">{comboItem.item_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">₹{comboItem.price}</span>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  const updated = [...offerForm.comboItems];
                                  if (updated[idx].quantity > 1) {
                                    updated[idx].quantity -= 1;
                                    setOfferForm({ ...offerForm, comboItems: updated });
                                  }
                                }}
                              >
                                -
                              </Button>
                              <span className="w-6 text-center text-sm font-bold">{comboItem.quantity}</span>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 w-6 p-0"
                                onClick={() => {
                                  const updated = [...offerForm.comboItems];
                                  updated[idx].quantity += 1;
                                  setOfferForm({ ...offerForm, comboItems: updated });
                                }}
                              >
                                +
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  setOfferForm({
                                    ...offerForm,
                                    comboItems: offerForm.comboItems.filter((_, i) => i !== idx)
                                  });
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 bg-orange-50 rounded border border-dashed border-orange-300">
                        <ShoppingBag className="h-8 w-8 mx-auto text-orange-400 mb-2" />
                        <p className="text-sm text-orange-600 font-medium">No items added yet</p>
                        <p className="text-xs text-orange-500">Use the dropdown above to add items to your combo</p>
                      </div>
                    )}
                    
                    {/* Price Comparison */}
                    {offerForm.comboItems.length > 0 && (
                      <div className="text-sm bg-green-50 border border-green-200 p-3 rounded">
                        <div className="flex justify-between text-gray-600">
                          <span>Individual items total:</span>
                          <span className="line-through">
                            ₹{offerForm.comboItems.reduce((sum, ci) => sum + (ci.price * ci.quantity), 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-bold text-green-700">
                          <span>Combo price:</span>
                          <span>₹{offerForm.comboPrice || '0.00'}</span>
                        </div>
                        {parseFloat(offerForm.comboPrice) > 0 && (
                          <div className="flex justify-between font-bold text-orange-700 mt-1 pt-1 border-t border-green-200">
                            <span>🎉 Customer saves:</span>
                            <span>
                              ₹{(offerForm.comboItems.reduce((sum, ci) => sum + (ci.price * ci.quantity), 0) - parseFloat(offerForm.comboPrice)).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* COMPLIMENTARY - special note */}
              {offerForm.type === 'COMPLIMENTARY' && (
                <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
                  <p className="text-sm text-pink-700 font-medium">🎁 Complimentary Order</p>
                  <p className="text-xs text-pink-600 mt-1">
                    When applied, the entire order will be marked as FREE (₹0). 
                    Use this for special occasions, promotional giveaways, or staff meals.
                  </p>
                </div>
              )}

              {/* Type-specific fields */}
              {(offerForm.type === 'PERCENT_DISCOUNT' || offerForm.type === 'HAPPY_HOUR' || offerForm.type === 'FIRST_ORDER' || offerForm.type === 'LOYALTY') && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Discount Percentage *</Label>
                    <Input 
                      type="number"
                      placeholder="10"
                      value={offerForm.value}
                      onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Min Order Value</Label>
                    <Input 
                      type="number"
                      placeholder="0"
                      value={offerForm.minOrderValue}
                      onChange={(e) => setOfferForm({ ...offerForm, minOrderValue: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Max Discount (₹)</Label>
                    <Input 
                      type="number"
                      placeholder="100"
                      value={offerForm.maxDiscount}
                      onChange={(e) => setOfferForm({ ...offerForm, maxDiscount: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {(offerForm.type === 'FLAT_DISCOUNT' || offerForm.type === 'CASHBACK') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{offerForm.type === 'CASHBACK' ? 'Cashback Amount (₹)' : 'Discount Amount (₹)'} *</Label>
                    <Input 
                      type="number"
                      placeholder="50"
                      value={offerForm.value}
                      onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Min Order Value</Label>
                    <Input 
                      type="number"
                      placeholder="0"
                      value={offerForm.minOrderValue}
                      onChange={(e) => setOfferForm({ ...offerForm, minOrderValue: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {offerForm.type === 'BOGO' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Buy Quantity</Label>
                    <Input 
                      type="number"
                      value={offerForm.buyQty}
                      onChange={(e) => setOfferForm({ ...offerForm, buyQty: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Get Quantity (Free)</Label>
                    <Input 
                      type="number"
                      value={offerForm.getQty}
                      onChange={(e) => setOfferForm({ ...offerForm, getQty: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Free Item (Optional)</Label>
                    <Select value={offerForm.getItemId} onValueChange={(v) => setOfferForm({ ...offerForm, getItemId: v })}>
                      <SelectTrigger><SelectValue placeholder="Same item" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Same item</SelectItem>
                        {menuItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {offerForm.type === 'BULK_DISCOUNT' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Min Quantity</Label>
                    <Input 
                      type="number"
                      placeholder="5"
                      value={offerForm.buyQty}
                      onChange={(e) => setOfferForm({ ...offerForm, buyQty: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Discount %</Label>
                    <Input 
                      type="number"
                      placeholder="15"
                      value={offerForm.value}
                      onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Applicable On</Label>
                    <Select value={offerForm.applicableItems} onValueChange={(v) => setOfferForm({ ...offerForm, applicableItems: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Items</SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="specific">Specific Items</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="datetime-local"
                    value={offerForm.startDate}
                    onChange={(e) => setOfferForm({ ...offerForm, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="datetime-local"
                    value={offerForm.endDate}
                    onChange={(e) => setOfferForm({ ...offerForm, endDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Usage Limit (0 = unlimited)</Label>
                  <Input 
                    type="number"
                    placeholder="100"
                    value={offerForm.usageLimit}
                    onChange={(e) => setOfferForm({ ...offerForm, usageLimit: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Per Customer Limit (0 = unlimited)</Label>
                  <Input 
                    type="number"
                    placeholder="1"
                    value={offerForm.perCustomerLimit}
                    onChange={(e) => setOfferForm({ ...offerForm, perCustomerLimit: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea 
                  placeholder="Describe the offer terms and conditions..."
                  value={offerForm.description}
                  onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={offerForm.enabled}
                    onCheckedChange={(checked) => setOfferForm({ ...offerForm, enabled: checked })}
                  />
                  <Label>Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={offerForm.autoApply}
                    onCheckedChange={(checked) => setOfferForm({ ...offerForm, autoApply: checked })}
                  />
                  <Label>Auto Apply (No code needed)</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleAddOffer}>
                {editingOffer ? 'Update Offer' : 'Create Offer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default OffersManager;
